import { CPENZLoader } from "@/components/CPENZLoader";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, ArrowRight, BarChart3, LayoutGrid, Lock, Rows3 } from "lucide-react";
import Navbar from "@/components/Navbar";
import { CPENZIcon } from "@/components/CPENZIcon";
import { Button } from "@/components/ui/button";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { useAuthDialog } from "@/context/AuthDialogContext";
import { AppNav } from "@/components/AppNav";
import { VaultCard, VAULT_FILTERS } from "@/components/VaultCard";
import { VaultFilters, DEFAULT_FILTERS, applyFilters } from "@/components/VaultFilters";
import { HScroll } from "@/components/ideaos/HScroll";
import api from "@/lib/api";

export default function Dashboard() {
  const { t, lang } = useLang();
  const { user, ready } = useAuth();
  const authDialog = useAuthDialog();
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [filter, setFilter] = useState("all");
  const [adv, setAdv] = useState(DEFAULT_FILTERS);
  const [view, setView] = useState(() => localStorage.getItem("cpenz_vault_view") || "grid");
  const setViewPersist = (v) => { setView(v); localStorage.setItem("cpenz_vault_view", v); };
  const FILTERS = VAULT_FILTERS.filter(([k]) => !["scored", "shared", "market"].includes(k));
  const visible = applyFilters((projects || []).filter(FILTERS.find((x) => x[0] === filter)[1]), adv);
  const patchProject = (id, patch) => setProjects((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const changeMaturity = async (p, maturity) => {
    setProjects((prev) => prev.map((x) => (x.id === p.id ? { ...x, maturity } : x)));
    try { await api.patch(`/projects/${p.id}/maturity`, { maturity }); } catch (e) { setProjects((prev) => prev.map((x) => (x.id === p.id ? { ...x, maturity: p.maturity } : x))); }
  };
  const [purchases, setPurchases] = useState([]);

  useEffect(() => {
    if (user) {
      api.get(`/projects?lang=${lang}`).then(({ data }) => setProjects(data)).catch(() => setProjects([]));
      api.get("/me/purchases").then(({ data }) => setPurchases(data)).catch(() => setPurchases([]));
    }
  }, [user, lang]);

  if (!ready || user === null) {
    return <div className="min-h-screen bg-white flex items-center justify-center"><CPENZLoader size={96} /></div>;
  }

  if (user === false) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar onStart={() => navigate("/app/new")} onLogin={() => authDialog.openAuth(() => navigate("/app/dashboard"))} />
        <div className="max-w-md mx-auto px-5 pt-16 text-center">
          <CPENZIcon size={80} />
          <h1 className="mt-6 cpenz-display text-3xl text-[#0b0f1a]">{t("auth.welcome")}</h1>
          <p className="mt-2 text-slate-500">{t("auth.subtitle")}</p>
          <Button data-testid="dash-login-btn" onClick={() => authDialog.openAuth(() => navigate("/app/dashboard"))} className="mt-6 rounded-full bg-[#0b0f1a] text-white px-6 h-11">
            {t("nav.login")} <ArrowRight size={16} className="ml-1" />
          </Button>
        </div>
      </div>
    );
  }

  const unlockedIds = new Set((projects || []).filter((p) => p.unlocked).map((p) => p.id));
  const pendingProjectIds = new Set(
    purchases.filter((o) => o.payment_status === "pending" && o.project_id && !unlockedIds.has(o.project_id)).map((o) => o.project_id)
  );
  const firstPending = [...pendingProjectIds][0];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar onStart={() => navigate("/app/new")} onLogin={() => authDialog.openAuth()} />
      <div className="flex-1 max-w-7xl mx-auto w-full px-5 lg:px-8 pt-3 md:pt-8 pb-16">
        {firstPending && (
          <div data-testid="dash-pending-banner" className="mb-8 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 h-9 w-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0"><Lock size={16} className="text-amber-700" /></div>
              <div>
                <p className="font-semibold text-[#0b0f1a]">{t("dash.pending_banner_title")}</p>
                <p className="mt-1 text-sm text-slate-500 max-w-xl">{t("dash.pending_banner_body")}</p>
              </div>
            </div>
            <Button data-testid="dash-pending-cta" onClick={() => navigate(`/app/checkout/${firstPending}`)} className="rounded-full bg-[#0b0f1a] text-white h-11 px-6 whitespace-nowrap">
              {t("dash.pending_banner_cta")} <ArrowRight size={15} className="ml-1" />
            </Button>
          </div>
        )}
        <div className="flex flex-col sm:flex-row items-center sm:items-end justify-center sm:justify-between gap-4">
          <div className="text-center sm:text-left w-full sm:w-auto">
            <span className="cpenz-overline">{t("dash.greeting")}</span>
            <h1 className="mt-1 cpenz-display text-3xl sm:text-4xl text-[#0b0f1a]" data-testid="dash-title">
              {t("dash.projects")} <span className="text-slate-300">{projects ? `· ${projects.length}` : ""}</span>
            </h1>
            <p className="mt-1 max-w-xl text-[11px] leading-relaxed text-slate-400" data-testid="dash-info">{t("dash.projects_info")}</p>
          </div>
          <div className="flex items-center gap-2">
            {user.role === "admin" && (
              <>
                <Button data-testid="dash-admin-btn" variant="outline" onClick={() => navigate("/app/admin")} className="rounded-full h-11 px-5 border-[#7c3aed] text-[#7c3aed] hover:bg-[#f7f5ff]">
                  <BarChart3 size={16} className="mr-1" /> {t("admin.link")}
                </Button>
                <Button data-testid="dash-admin-traffic-btn" variant="outline" onClick={() => navigate("/app/admin/traffic")} className="rounded-full h-11 px-5 border-[#7c3aed] text-[#7c3aed] hover:bg-[#f7f5ff]">
                  <Activity size={16} className="mr-1" /> {t("admin.traffic.title")}
                </Button>
              </>
            )}
          </div>
        </div>

        {projects === null ? (
          <div className="mt-16 flex justify-center"><CPENZLoader size={80} /></div>
        ) : projects.length === 0 ? (
          <div className="mt-16 rounded-3xl border border-dashed border-slate-200 p-16 text-center" data-testid="dash-empty">
            <CPENZIcon size={70} />
            <p className="mt-5 text-slate-500">{t("dash.empty")}</p>
            <Button onClick={() => navigate("/app/home")} className="mt-5 rounded-full bg-[#0b0f1a] text-white px-6 h-11">{t("dash.empty_cta")} →</Button>
          </div>
        ) : (
          <>
          <div className="mt-4 md:mt-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 -mx-5 px-5 lg:mx-0 lg:px-0" data-testid="vault-filters">
              {FILTERS.map(([k, fn]) => {
                const n = projects.filter(fn).length;
                if (n === 0 && k !== "all") return null;
                return (
                  <button key={k} data-testid={`vault-filter-${k}`} onClick={() => setFilter(k)} aria-pressed={filter === k}
                    className={`shrink-0 text-xs font-semibold rounded-full px-4 py-2 border transition-colors ${filter === k ? "bg-[#0b0f1a] text-white border-[#0b0f1a]" : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"}`}>
                    {t(`vault.${k}`)} <span className={filter === k ? "text-slate-300" : "text-slate-400"}>· {n}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <VaultFilters value={adv} onChange={setAdv} projects={projects} />
              <div className="inline-flex rounded-full bg-slate-100 p-1" role="group" data-testid="vault-view">
                <button type="button" onClick={() => setViewPersist("grid")} aria-pressed={view === "grid"} aria-label={t("vault.view_grid")} data-testid="vault-view-grid" className={`inline-flex h-8 w-9 items-center justify-center rounded-full ${view === "grid" ? "bg-white text-[#0b0f1a] shadow-sm" : "text-slate-500"}`}><LayoutGrid size={14} /></button>
                <button type="button" onClick={() => setViewPersist("rows")} aria-pressed={view === "rows"} aria-label={t("vault.view_rows")} data-testid="vault-view-rows" className={`inline-flex h-8 w-9 items-center justify-center rounded-full ${view === "rows" ? "bg-white text-[#0b0f1a] shadow-sm" : "text-slate-500"}`}><Rows3 size={14} /></button>
              </div>
            </div>
          </div>
          {visible.length === 0 && <p className="mt-8 text-slate-400" data-testid="vault-empty-filter">{t("vault.empty_filter")}</p>}
          {view === "grid" ? (
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 md:grid-cols-3 xl:grid-cols-4" data-testid="vault-grid">
              {visible.map((p) => <VaultCard key={p.id} p={p} pending={pendingProjectIds.has(p.id)} onOpen={() => navigate(p.score ? `/app/project/${p.id}` : `/app/idea/${p.id}`)} onMaturity={(m) => changeMaturity(p, m)} onDeleted={() => setProjects((prev) => prev.filter((x) => x.id !== p.id))} onCover={(c, auto) => patchProject(p.id, { cover_url: c, cover_auto: !!auto })} onRenamed={(n) => patchProject(p.id, { name: n })} onVisibility={(v) => patchProject(p.id, { visibility: v })} onFavorite={(f) => patchProject(p.id, { favorite: f })} />)}
            </div>
          ) : (
            <div data-testid="vault-rows">
              {FILTERS.filter(([k]) => !["all", "favorites", "scored", "shared", "market"].includes(k)).map(([k, fn]) => {
                const rows = visible.filter(fn);
                if (!rows.length) return null;
                return (
                  <HScroll key={k} testid={`vault-row-${k}`} title={`${t(`vault.${k}`)} · ${rows.length}`} itemClass="w-[68vw] sm:w-[240px]">
                    {rows.map((p) => <VaultCard key={p.id} p={p} compact pending={pendingProjectIds.has(p.id)} onOpen={() => navigate(p.score ? `/app/project/${p.id}` : `/app/idea/${p.id}`)} onMaturity={(m) => changeMaturity(p, m)} onDeleted={() => setProjects((prev) => prev.filter((x) => x.id !== p.id))} onCover={(c, auto) => patchProject(p.id, { cover_url: c, cover_auto: !!auto })} onRenamed={(n) => patchProject(p.id, { name: n })} onVisibility={(v) => patchProject(p.id, { visibility: v })} onFavorite={(f) => patchProject(p.id, { favorite: f })} />)}
                  </HScroll>
                );
              })}
            </div>
          )}
          </>
        )}

      </div>
      <AppNav />
    </div>
  );
}
